import TableCell from '@material-ui/core/TableCell'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { AgencySettings_EnabledFeaturesEnum, PolicyRule_VehicleTypesEnum } from 'src/api/gen/api'
import { IPolicyRuleGeofence } from 'src/api/types/policy'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { TRulesValidation } from 'src/commons/policies/RulesValidation'
import { SimpleTable } from 'src/components/Table'
import { useCheckEnabledFeature } from 'src/layout/AgencySettings'

import { FieldTitle } from '../../commons'
import { GeographiesField, RuleNameField, VehicleTypesField } from '../fields'

type Row = [
  TRulesValidation,
  string, // name
  PolicyRule_VehicleTypesEnum[], // vehicle_types
  string[], // geographies
  string[] // buffered_geographies
]

const BaseRule = (props: IBaseTableProps<IPolicyRuleGeofence>) => {
  const { rules, allGeographies, disabled, validation } = props
  const { t } = useTranslation()
  const buffered_areas_enabled = useCheckEnabledFeature()(AgencySettings_EnabledFeaturesEnum.buffered_areas)

  const HEADERS: [string, string?][] = [
    [t('rule.name'), undefined],
    [t('rule.count.vehicle_types'), undefined],
    [t('rule.geographies'), t('rule.geographies.tooltip')],
  ]

  if (buffered_areas_enabled) {
    HEADERS.push([t('rule.buffered_geographies'), t('rule.buffered_geographies.tooltip')])
  }

  const handleChangeRule =
    (key: keyof IPolicyRuleGeofence) =>
    (val: string | PolicyRule_VehicleTypesEnum[] | string[]): void => {
      const newRule: IPolicyRuleGeofence[] = [{ ...rules[0], [key]: val }]
      props.handleChange(newRule)
    }

  const items = rules.map(
    (rule: IPolicyRuleGeofence, idx): Row => [
      validation[idx],
      rule.name,
      rule.vehicle_types,
      rule.geographies,
      rule.buffered_geographies,
    ]
  )
  return (
    <SimpleTable
      headerRow={HEADERS.map(([title, tooltip]) => (
        <TableCell key={`key-${title}`}>
          <FieldTitle title={title} tooltip={tooltip} />
        </TableCell>
      ))}
      items={items}
      rowRenderer={([ruleValidation, name, vehicleTypes, geographies, buffered_geographies]: Row) => (
        <>
          <TableCell style={{ height: '100px', verticalAlign: 'bottom' }} align="justify">
            <RuleNameField
              value={name}
              handleChange={handleChangeRule('name')}
              disabled={disabled}
              error={!ruleValidation.isFieldValid('name')}
              helperText={ruleValidation.getFieldErrorMsg('name')}
            />
          </TableCell>

          <TableCell style={{ height: '100px', verticalAlign: 'bottom' }} align="justify">
            <VehicleTypesField
              value={vehicleTypes}
              handleChange={handleChangeRule('vehicle_types')}
              disabled={disabled}
              error={!ruleValidation.isFieldValid('vehicle_types')}
              helperText={ruleValidation.getFieldErrorMsg('vehicle_types')}
            />
          </TableCell>

          <TableCell
            align="justify"
            style={{
              maxWidth: '400px',
              height: '100px',
              verticalAlign: 'bottom',
            }}
          >
            <GeographiesField
              value={geographies}
              allGeographies={allGeographies}
              handleChange={handleChangeRule('geographies')}
              disabled={disabled}
              error={!ruleValidation.isFieldValid('geographies')}
              helperText={ruleValidation.getFieldErrorMsg('geographies')}
            />
          </TableCell>

          {buffered_areas_enabled && (
            <TableCell
              align="justify"
              style={{
                maxWidth: '400px',
                height: '100px',
                verticalAlign: 'bottom',
              }}
            >
              <GeographiesField
                value={buffered_geographies}
                allGeographies={allGeographies}
                handleChange={handleChangeRule('buffered_geographies')}
                disabled={disabled}
                helperText={t('rule.buffered_geographies.help')}
              />
            </TableCell>
          )}
        </>
      )}
    />
  )
}

export default BaseRule
